import { Fragment, useMemo, useRef, useState } from 'react';
import { FontIcon } from '@fluentui/react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DrawerBody,
  DrawerHeader,
  DrawerHeaderTitle,
  FluentProvider,
  MessageBar,
  MessageBarBody,
  OverlayDrawer,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';
import {
  PagedDataGrid,
  useActiveTheme,
  useIsMobile,
  type DataGridColumn,
  type ICommandBarItem,
  type IPagedCollection,
  type IPagedDataGridRefType,
  type IRowMenuOption,
} from '@moko/ui';
import {
  apiStatus,
  slaFailureMessage,
  cutoffApplies,
  dueTimeApplies,
  effectiveSla,
  formatAllowance,
  formatWallTime,
  rulesByServiceId,
  type ITimeServiceRule,
} from '../../services/slaRule';
import {
  getAllTimeServices,
  getTenantTimeServiceRules,
  getTimeServiceRules,
} from '../../services/slaRuleService';
import SlaRuleForm from './SlaRuleForm';

const useStyles = makeStyles({
  stack: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap(tokens.spacingVerticalM),
  },
  cell: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  note: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  warning: {
    color: tokens.colorStatusWarningForeground1,
    fontSize: tokens.fontSizeBase200,
  },
  details: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    ...shorthands.gap(tokens.spacingHorizontalXL),
    '@media (max-width: 640px)': {
      gridTemplateColumns: '1fr',
    },
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap(tokens.spacingVerticalS),
    minWidth: 0,
  },
  sectionTitle: {
    margin: 0,
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
  },
  fields: {
    display: 'grid',
    gridTemplateColumns: '72px 1fr',
    rowGap: tokens.spacingVerticalXS,
    columnGap: tokens.spacingHorizontalS,
    margin: 0,
  },
  fieldLabel: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  fieldValue: {
    margin: 0,
    fontWeight: tokens.fontWeightSemibold,
  },
  empty: {
    margin: 0,
    color: tokens.colorNeutralForeground3,
  },
});

type SlaGridRow = {
  id: number;
  name: string;
  isActive: boolean;
  tenantRule?: ITimeServiceRule;
  globalRule?: ITimeServiceRule;
};

const usedLabel = {
  global: 'Global rule',
  tenant: 'Tenant override',
  none: 'Not configured',
} as const;

const cell = (value: string) => value || '—';

const ruleFields = (rule: ITimeServiceRule) => {
  const site = formatAllowance(rule.siteDueOffset, rule.siteDueOffsetUnit);
  return [
    { label: 'Site', value: site || 'Site rule not configured' },
    {
      label: 'Results',
      value:
        formatAllowance(rule.resultsDueOffset, rule.resultsDueOffsetUnit) ||
        '—',
    },
    {
      label: 'Due',
      value: formatWallTime(rule.dueTime) || rule.dueTime || '—',
    },
    {
      label: 'Cutoff',
      value:
        formatWallTime(rule.orderCutoffTime) || rule.orderCutoffTime || '—',
    },
    { label: 'State', value: rule.isActive ? 'Active' : 'Inactive' },
  ];
};

const effectiveCells = (
  rule: ITimeServiceRule | null,
  siteUnresolved: boolean
) => {
  if (!rule) {
    return { site: '—', results: '—', due: '—', cutoff: '—' };
  }
  const site = siteUnresolved
    ? 'Site rule not configured'
    : formatAllowance(rule.siteDueOffset, rule.siteDueOffsetUnit);
  return {
    site: cell(site),
    results: cell(
      formatAllowance(rule.resultsDueOffset, rule.resultsDueOffsetUnit)
    ),
    due: dueTimeApplies(rule)
      ? cell(formatWallTime(rule.dueTime))
      : 'Not applicable',
    cutoff: cutoffApplies(rule)
      ? cell(formatWallTime(rule.orderCutoffTime))
      : 'Not applicable',
  };
};

const slaView = (row: SlaGridRow) => {
  const effective = effectiveSla(row.tenantRule, row.globalRule);
  return {
    ...effective,
    ...effectiveCells(effective.rule, effective.siteUnresolved),
    rule: usedLabel[effective.used],
  };
};

const RuleSection = ({
  title,
  rule,
  empty,
  styles,
}: {
  title: string;
  rule: ITimeServiceRule | undefined;
  empty: string;
  styles: ReturnType<typeof useStyles>;
}) => (
  <section className={styles.section}>
    <h3 className={styles.sectionTitle}>{title}</h3>
    {rule ? (
      <dl className={styles.fields}>
        {ruleFields(rule).map((field) => (
          <Fragment key={field.label}>
            <dt className={styles.fieldLabel}>{field.label}</dt>
            <dd className={styles.fieldValue}>{field.value}</dd>
          </Fragment>
        ))}
      </dl>
    ) : (
      <p className={styles.empty}>{empty}</p>
    )}
  </section>
);

const loadSlaGrid = async (
  abortController: AbortController,
  pageSize: number
): Promise<IPagedCollection<SlaGridRow>> => {
  const [services, globalRules, tenantRules] = await Promise.all([
    getAllTimeServices(abortController),
    getTimeServiceRules(abortController),
    getTenantTimeServiceRules(abortController),
  ]);
  const globalById = rulesByServiceId(globalRules);
  const tenantById = rulesByServiceId(tenantRules);
  const items = services.map((service) => ({
    id: service.id,
    name: service.name,
    isActive: service.isActive,
    tenantRule: tenantById.get(service.id),
    globalRule: globalById.get(service.id),
  }));
  return {
    items,
    pageNo: 1,
    pageSize,
    pageCount: 1,
    rowsCount: items.length,
    isLastPage: true,
  };
};

const SlaRuleEditor = () => {
  const styles = useStyles();
  const theme = useActiveTheme();
  const isMobile = useIsMobile();
  const gridRef = useRef<IPagedDataGridRefType>(null);
  const [listError, setListError] = useState<unknown>();
  const [editing, setEditing] = useState<SlaGridRow | null>(null);
  const [details, setDetails] = useState<SlaGridRow | null>(null);
  const forbidden = apiStatus(listError) === 403;
  const loadMessage = slaFailureMessage([
    { error: listError, fallback: 'Failed to load SLA rules.' },
  ]);
  const columns = useMemo<DataGridColumn<SlaGridRow>[]>(
    () => [
      {
        key: 'name',
        name: 'Time Service',
        minWidth: 160,
        width: 200,
        renderCell: (row) => (
          <span className={styles.cell}>
            <span>{row.name}</span>
            {!row.isActive && (
              <span className={styles.note}>Inactive Time Service</span>
            )}
          </span>
        ),
      },
      {
        key: 'site',
        name: 'Site allowance',
        minWidth: 140,
        width: 180,
        renderCell: (row) => {
          const view = slaView(row);
          return (
            <span className={styles.cell}>
              <span>{view.site}</span>
              {view.siteUnresolved && (
                <span className={styles.warning}>
                  Site deadlines cannot be calculated.
                </span>
              )}
            </span>
          );
        },
      },
      {
        key: 'results',
        name: 'Results allowance',
        minWidth: 140,
        width: 170,
        value: (row) => slaView(row).results,
      },
      {
        key: 'due',
        name: 'Due time',
        minWidth: 110,
        width: 120,
        value: (row) => slaView(row).due,
      },
      {
        key: 'cutoff',
        name: 'Order cutoff',
        minWidth: 110,
        width: 130,
        value: (row) => slaView(row).cutoff,
      },
      {
        key: 'rule',
        name: 'Rule used',
        minWidth: 140,
        width: 180,
        renderCell: (row) => {
          const view = slaView(row);
          return (
            <span className={styles.cell}>
              <span>{view.rule}</span>
              {view.disabledOverride && (
                <span className={styles.note}>
                  Tenant override saved but disabled
                </span>
              )}
            </span>
          );
        },
      },
    ],
    [styles]
  );
  const commands = useMemo<ICommandBarItem[]>(
    () => [
      {
        key: 'refresh',
        text: 'Refresh',
        icon: <FontIcon iconName="Refresh" />,
        onClick: (ctx) => ctx.refresh(),
      },
    ],
    []
  );
  const rowMenu = useMemo<IRowMenuOption<SlaGridRow>[]>(
    () => [
      {
        key: 'edit',
        label: 'Edit',
        icon: <FontIcon iconName="Edit" />,
        disabled: (row) => !row.isActive || forbidden,
        onClick: (row) => setEditing(row),
      },
      {
        key: 'details',
        label: 'View rule details',
        icon: <FontIcon iconName="Info" />,
        onClick: (row) => setDetails(row),
      },
    ],
    [forbidden]
  );

  return (
    <div className={styles.stack}>
      {loadMessage && (
        <MessageBar intent="error">
          <MessageBarBody>{loadMessage}</MessageBarBody>
        </MessageBar>
      )}
      <PagedDataGrid<SlaGridRow>
        ref={gridRef}
        columns={columns}
        getAction={loadSlaGrid}
        searchable={false}
        commandBarItems={commands}
        rowMenuOptions={rowMenu}
        fillContainer
        onLoading={() => setListError(undefined)}
        onError={setListError}
        onRowDoubleClicked={(row) => {
          if (row.isActive && !forbidden) setEditing(row);
        }}
        empty={{
          title: listError
            ? 'SLA rules could not be loaded.'
            : 'No time services configured.',
        }}
      />
      <FluentProvider theme={theme}>
        <OverlayDrawer
          open={!!editing}
          onOpenChange={(_, data) => {
            if (!data.open) setEditing(null);
          }}
          position="end"
          size={isMobile ? 'full' : 'medium'}
        >
          <DrawerHeader>
            <DrawerHeaderTitle
              action={
                <Button
                  appearance="subtle"
                  aria-label="Close"
                  icon={<Dismiss24Regular />}
                  onClick={() => setEditing(null)}
                />
              }
            >
              {editing ? `SLA rule — ${editing.name}` : 'SLA rule'}
            </DrawerHeaderTitle>
          </DrawerHeader>
          <DrawerBody>
            {editing && (
              <SlaRuleForm
                key={editing.id}
                timeServiceId={editing.id}
                tenantRule={editing.tenantRule}
                globalRule={editing.globalRule}
                onClose={() => setEditing(null)}
                onSaved={() => {
                  setEditing(null);
                  gridRef.current?.refresh();
                }}
              />
            )}
          </DrawerBody>
        </OverlayDrawer>
      </FluentProvider>
      <Dialog
        open={!!details}
        onOpenChange={(_, data) => {
          if (!data.open) setDetails(null);
        }}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>
              {details ? `SLA rule — ${details.name}` : 'SLA rule'}
            </DialogTitle>
            <DialogContent>
              {details && (
                <div className={styles.details}>
                  <RuleSection
                    title="Global rule"
                    rule={details.globalRule}
                    empty="No global rule saved."
                    styles={styles}
                  />
                  <RuleSection
                    title="Tenant override"
                    rule={details.tenantRule}
                    empty="No tenant override saved."
                    styles={styles}
                  />
                </div>
              )}
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setDetails(null)}>
                Close
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};

export default SlaRuleEditor;
